import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';

import { cn } from '@shadcn/utils';

type TooltipVariant = 'blue' | 'dark' | 'light';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    variant?: TooltipVariant;
  }
>(({ className, sideOffset = 4, variant = 'light', ...props }, ref) => {
  const variantClasses: Record<TooltipVariant, string> = {
    blue: 'bg-sy-blue-60 text-white',
    dark: 'bg-sy-neutral-80 text-white',
    light: 'bg-white text-sy-neutral-80',
  };

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'z-50 overflow-hidden rounded-md px-3 py-1.5 text-xs animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          variantClasses[variant],
          className
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
});

const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Arrow> & {
    align?: 'start' | 'end';
    side?: 'top' | 'bottom';
    variant?: TooltipVariant;
  }
>(({ className, align, variant = 'light', ...props }, ref) => {
  const variantClasses: Record<TooltipVariant, string> = {
    blue: 'fill-sy-blue-60',
    dark: 'fill-sy-neutral-80',
    light: 'fill-white',
  };

  return (
    <div className={cn({ ['[&>span]:w-[80%]']: typeof align !== 'undefined' })}>
      <TooltipPrimitive.Arrow
        ref={ref}
        className={cn(
          'fill-current',
          { ['ml-auto']: align === 'start' },
          variantClasses[variant],
          className
        )}
        {...props}
      />
    </div>
  );
});

TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger };
